import React from 'react';
import { Select, Checkbox, InputNumber } from 'antd';
import { FP120, FP121, FP122 } from '../../../services/pwp211Api';

interface Tab2Props {
  formData: FP120;
  judgments: FP121[];
  defects: FP122[];
  isEditing: boolean;
  getField: (field: keyof FP120) => unknown;
  setField: (field: keyof FP120, value: unknown) => void;
  updateJudgmentField: (seq: number, field: keyof FP121, value: unknown) => void;
  addJudgmentRow: () => void;
  deleteJudgmentRow: (seq: number) => void;
  setDefects: React.Dispatch<React.SetStateAction<FP122[]>>;
}

const INSP_RESULT_OPTIONS = [
  { value: 1, label: 'A' },
  { value: 2, label: 'B' },
  { value: 3, label: 'NG' },
];

/* ---- style constants ---- */
const lblStyle: React.CSSProperties = { width: 130, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 };

const Tab2Judgment: React.FC<Tab2Props> = React.memo(({
  formData, judgments, defects, isEditing,
  getField, setField,
  updateJudgmentField, addJudgmentRow, deleteJudgmentRow, setDefects,
}) => {
  const handleDefectField = (defectKey: string, field: keyof FP122, value: unknown) => {
    const [seq02, seq03] = defectKey.split('-').map(Number);
    setDefects(prev => prev.map(d =>
      d.fp122_02 === seq02 && d.fp122_03 === seq03 ? { ...d, [field]: value } : d
    ));
  };

  const addDefectRow = (seq: number) => {
    const existingDefects = defects.filter(d => d.fp122_02 === seq);
    const newSeq = existingDefects.length + 1;
    const newDefect: FP122 = {
      fp122_01: '', fp122_02: seq, fp122_03: newSeq,
      fp122_11: 0, fp122_12: 0, fp122_13: '', fp122_14: 0,
      fp122_15: '', fp122_16: 0, fp122_91: '', fp122_92: null, fp122_93: '',
    };
    setDefects(prev => [...prev, newDefect]);
  };

  const removeDefectRow = (seq02: number, seq03: number) => {
    setDefects(prev => prev.filter(d => !(d.fp122_02 === seq02 && d.fp122_03 === seq03)));
  };

  // Active judgment row (first selected or first row)
  const activeJudgment = judgments.length > 0 ? judgments[0] : null;
  const activeSeq = activeJudgment?.fp121_02 ?? 0;

  return (
    <div className="pwp211-tab2">
      {/* Page2 standalone fields -- FoxPro layout matching spatial positions */}

      {/* Row: 總項數 (right area, top=137) */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_22" style={lblStyle}>總項數</span>
          <input
            value={(getField('fp120_22') as number) || 0}
            readOnly
            disabled
            style={{ width: 60, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: '#f5f5f5', color: '#666' }} data-contract-field="fp120_22"
          />
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp121_241" style={lblStyle}>色樣說明</span>
          <input
            value={activeJudgment?.fp121_241 || ''}
            onChange={e => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_241', e.target.value)}
            disabled={!isEditing}
            maxLength={30}
            style={{ width: 200, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: isEditing ? '#fff' : '#f5f5f5' }} data-contract-field="fp121_241"
          />
        </div>
      </div>

      {/* Row: 幅寬-針孔內/外/可用 (fp121_111 / fp121_112 / fp121_113) */}
      <div style={rowStyle}>
        <span className="form-label" data-contract-field="fp121_111" style={lblStyle}>幅寬-針孔內/外/可用</span>
        <InputNumber
          value={activeJudgment?.fp121_111 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_111', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_111"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_112 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_112', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_112"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_113 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_113', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_113"
        />
        <span className="form-label" data-contract-field="fp121_29" style={{ marginLeft: 24, ...lblStyle, width: 'auto' }}>單筆檢驗結果</span>
        <Select
          value={activeJudgment?.fp121_29 ?? 0}
          onChange={val => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_29', val)}
          disabled={!isEditing}
          style={{ width: 80 }}
          options={[
            { value: 0, label: '-- --' },
            ...INSP_RESULT_OPTIONS,
          ]} data-contract-field="fp121_29"
        />
      </div>

      {/* Row: 實際碼重 (fp121_12) */}
      <div style={rowStyle}>
        <span className="form-label" data-contract-field="fp121_12" style={lblStyle}>實際碼重</span>
        <InputNumber
          value={activeJudgment?.fp121_12 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_12', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_12"
        />
      </div>

      {/* Row: 米平方重-左/中/右 (fp121_131 / fp121_132 / fp121_133) */}
      <div style={rowStyle}>
        <span className="form-label" data-contract-field="fp121_131" style={lblStyle}>米平方重-左/中/右</span>
        <InputNumber
          value={activeJudgment?.fp121_131 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_131', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_131"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_132 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_132', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_132"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_133 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_133', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_133"
        />
        <span className="form-label" data-contract-field="fp120_30" style={{ marginLeft: 24, fontSize: 13, color: '#333' }}>檢驗狀況</span>
        <Checkbox
          checked={formData.fp120_30}
          onChange={e => setField('fp120_30', e.target.checked)}
          disabled={!isEditing} data-contract-field="fp120_30"
        />
      </div>

      {/* Row: 種類 (fp121_17 / fp121_18) */}
      <div style={rowStyle}>
        <span className="form-label" data-contract-field="fp121_17" style={lblStyle}>種類</span>
        <InputNumber
          value={activeJudgment?.fp121_17 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_17', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_17"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_18 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_18', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_18"
        />
      </div>

      {/* Row: 原碼數/公斤/米數 (fp121_15 / fp121_151 / fp121_152) */}
      <div style={rowStyle}>
        <span className="form-label" data-contract-field="fp121_15" style={lblStyle}>原碼數/公斤/米數</span>
        <InputNumber
          value={activeJudgment?.fp121_15 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_15', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_15"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_151 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_151', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_151"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_152 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_152', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_152"
        />
      </div>

      {/* Row: 實際碼數/公斤/米數 (fp121_14 / fp121_141 / fp121_142) */}
      <div style={rowStyle}>
        <span className="form-label" data-contract-field="fp121_14" style={lblStyle}>實際碼數/公斤/米數</span>
        <InputNumber
          value={activeJudgment?.fp121_14 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_14', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_14"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_141 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_141', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_141"
        />
        <span>/</span>
        <InputNumber
          value={activeJudgment?.fp121_142 ?? 0}
          onChange={v => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_142', v || 0)}
          disabled={!isEditing}
          style={{ width: 80 }} data-contract-field="fp121_142"
        />
      </div>

      {/* Row: 等級判定 1./2./3./4. (fp121_31-34) -- FoxPro labels are "1.","2.","3.","4." */}
      <div style={rowStyle}>
        <span style={lblStyle}> </span>
        <span style={{ fontSize: 13, color: '#333' }}>1.</span>
        <input
          value={activeJudgment?.fp121_31 || ''}
          onChange={e => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_31', e.target.value)}
          disabled={!isEditing}
          maxLength={4}
          style={{ width: 60, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: isEditing ? '#fff' : '#f5f5f5' }} data-contract-field="fp121_31"
        />
        <span style={{ fontSize: 13, color: '#333' }}>2.</span>
        <input
          value={activeJudgment?.fp121_32 || ''}
          onChange={e => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_32', e.target.value)}
          disabled={!isEditing}
          maxLength={4}
          style={{ width: 60, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: isEditing ? '#fff' : '#f5f5f5' }} data-contract-field="fp121_32"
        />
        <span style={{ fontSize: 13, color: '#333' }}>3.</span>
        <input
          value={activeJudgment?.fp121_33 || ''}
          onChange={e => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_33', e.target.value)}
          disabled={!isEditing}
          maxLength={4}
          style={{ width: 60, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: isEditing ? '#fff' : '#f5f5f5' }} data-contract-field="fp121_33"
        />
        <span style={{ fontSize: 13, color: '#333' }}>4.</span>
        <input
          value={activeJudgment?.fp121_34 || ''}
          onChange={e => activeSeq > 0 && updateJudgmentField(activeSeq, 'fp121_34', e.target.value)}
          disabled={!isEditing}
          maxLength={4}
          style={{ width: 60, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: isEditing ? '#fff' : '#f5f5f5' }} data-contract-field="fp121_34"
        />
      </div>

      <div className="tab2-actions">
        {isEditing && (
          <button onClick={addJudgmentRow} className="btn-sm btn-primary">明細新增</button>
        )}
      </div>

      {/* FP121 判定 Grid -- FoxPro column order: 項數, 缸號, 疋號, 色號, 接疋次數, 胚布等級, 成品等級, 手感, 色樣, 瑕疵因, 總扣點 */}
      <table className="detail-table">
        <thead>
          <tr>
            <th data-contract-field="fp121_02">項數</th>
            <th data-contract-field="fp121_28">缸號</th>
            <th data-contract-field="fp121_19">疋號</th>
            <th data-contract-field="fp121_30">色號</th>
            <th data-contract-field="fp121_20">接疋次數</th>
            <th data-contract-field="fp121_21">胚布等級</th>
            <th data-contract-field="fp121_22">成品等級</th>
            <th data-contract-field="fp121_23">手感</th>
            <th data-contract-field="fp121_24">色樣</th>
            <th data-contract-field="fp121_02">瑕疵因</th>
            <th data-contract-field="fp121_25">總扣點</th>
            {isEditing && <th>操作</th>}
          </tr>
        </thead>
        <tbody>
          {judgments.map((j) => (
            <React.Fragment key={j.fp121_02}>
              <tr>
                <td>{j.fp121_02}</td>
                <td>
                  <input
                    value={j.fp121_28}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_28', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_28"
                  />
                </td>
                <td>
                  <input
                    value={j.fp121_19}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_19', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_19"
                  />
                </td>
                <td>
                  <input
                    value={j.fp121_30}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_30', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_30"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={j.fp121_20}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_20', parseInt(e.target.value) || 0)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_20"
                  />
                </td>
                <td>
                  <input
                    value={j.fp121_21}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_21', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_21"
                  />
                </td>
                <td>
                  <input
                    value={j.fp121_22}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_22', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_22"
                  />
                </td>
                <td>
                  <input
                    value={j.fp121_23}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_23', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_23"
                  />
                </td>
                <td>
                  <input
                    value={j.fp121_24}
                    onChange={e => updateJudgmentField(j.fp121_02, 'fp121_24', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp121_24"
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  {/* 瑕疵因 column is a button column in FoxPro (Command1), opens PWP211FA */}
                  {isEditing && (
                    <button
                      onClick={() => addDefectRow(j.fp121_02)}
                      className="btn-sm btn-secondary"
                      title="加瑕疵"
                    >+</button>
                  )}
                </td>
                <td>{j.fp121_25}</td>
                {isEditing && (
                  <td>
                    <button onClick={() => deleteJudgmentRow(j.fp121_02)} className="btn-sm btn-danger">刪除</button>
                  </td>
                )}
              </tr>
              {/* FP122 瑕疵因明細 (sub-rows) */}
              {defects.filter(d => d.fp122_02 === j.fp121_02).map(d => (
                <tr key={`d-${d.fp122_02}-${d.fp122_03}`} className="defect-row">
                  <td colSpan={2} className="defect-label">  瑕疵#{d.fp122_03}</td>
                  <td colSpan={2}>
                    <span>起碼:</span>
                    <input
                      type="number"
                      value={d.fp122_11}
                      onChange={e => handleDefectField(`${d.fp122_02}-${d.fp122_03}`, 'fp122_11', parseFloat(e.target.value) || 0)}
                      disabled={!isEditing}
                      className="table-input small" data-contract-field="fp122_11"
                    />
                  </td>
                  <td colSpan={2}>
                    <span>迄碼:</span>
                    <input
                      type="number"
                      value={d.fp122_12}
                      onChange={e => handleDefectField(`${d.fp122_02}-${d.fp122_03}`, 'fp122_12', parseFloat(e.target.value) || 0)}
                      disabled={!isEditing}
                      className="table-input small" data-contract-field="fp122_12"
                    />
                  </td>
                  <td colSpan={2}>
                    <span>代碼:</span>
                    <input
                      value={d.fp122_13}
                      onChange={e => handleDefectField(`${d.fp122_02}-${d.fp122_03}`, 'fp122_13', e.target.value)}
                      disabled={!isEditing}
                      className="table-input small" data-contract-field="fp122_13"
                    />
                  </td>
                  <td colSpan={2}>
                    <span>扣分:</span>
                    <input
                      type="number"
                      value={d.fp122_14}
                      onChange={e => handleDefectField(`${d.fp122_02}-${d.fp122_03}`, 'fp122_14', parseInt(e.target.value) || 0)}
                      disabled={!isEditing}
                      className="table-input small" data-contract-field="fp122_14"
                    />
                  </td>
                  <td></td>
                  {isEditing && (
                    <td>
                      <button onClick={() => removeDefectRow(d.fp122_02, d.fp122_03)} className="btn-sm btn-danger">刪瑕疵</button>
                    </td>
                  )}
                </tr>
              ))}
            </React.Fragment>
          ))}
          {judgments.length === 0 && (
            <tr><td colSpan={isEditing ? 12 : 11} className="no-data">無判定明細</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
});

export default Tab2Judgment;
